import { useMutation, useQuery } from '@tanstack/react-query';
import { Copy, KeyRound, Printer as PrinterIcon, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field } from '@/components/ui/form-field';
import { Card, CardContent, CardHeader, CardTitle, Input, Select } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export interface PrinterItem {
  id: string;
  name: string;
  uri: string;
  location: string | null;
  username: string | null;
  hasPassword: boolean;
  media: string | null;
  copies: number;
  verifyTls: boolean;
  defaultFor: string[];
  active: boolean;
  status: string | null;
  lastError: string | null;
  lastCheckedAt: string | null;
}
export interface PrinterList {
  items: PrinterItem[];
  documents: { key: string; label: string }[];
}

export const usePrinters = (enabled = true) => useQuery({ queryKey: ['printers'], queryFn: () => api<PrinterList>('/printers'), enabled, staleTime: 60_000 });

/** Dialog „Drucken“: Beleg, Drucker (Standard vorbelegt) und Anzahl. */
export function PrintDialog({
  open,
  onClose,
  documents,
  target,
}: {
  open: boolean;
  onClose: () => void;
  documents: { key: string; label: string }[];
  target: { repairId?: string; contractId?: string; ticketId?: string };
}) {
  const { data } = usePrinters(open);
  const printers = data?.items.filter((p) => p.active) ?? [];
  const [doc, setDoc] = useState(documents[0]?.key ?? '');
  const [printerId, setPrinterId] = useState('');
  const [copies, setCopies] = useState(1);
  const defaultFor = (d: string) => printers.find((p) => p.defaultFor.includes(d));
  useEffect(() => {
    if (!open) return;
    const d = documents[0]?.key ?? '';
    setDoc(d);
    const p = defaultFor(d) ?? printers[0];
    setPrinterId(p?.id ?? '');
    setCopies(p?.copies ?? 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);
  const print = useMutation({
    mutationFn: () => api<{ ok: boolean; printer: string; error: string | null }>('/printers/print', { method: 'POST', body: { document: doc, ...target, printerId: printerId || null, copies } }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(`An „${r.printer}“ gesendet`);
        onClose();
      } else toast.error(r.error ?? 'Druck fehlgeschlagen');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Drucken</DialogTitle>
          <DialogDescription>Der Beleg wird erzeugt und direkt an den Drucker gesendet.</DialogDescription>
        </DialogHeader>
        <DialogBody className="grid gap-4">
          {printers.length === 0 ? (
            <p className="text-sm text-muted-foreground">Es ist noch kein Drucker eingerichtet (Administration → Drucker).</p>
          ) : (
            <>
              {documents.length > 1 && (
                <Field label="Beleg" htmlFor="pr-doc">
                  <Select
                    id="pr-doc"
                    value={doc}
                    onChange={(e) => {
                      setDoc(e.target.value);
                      const p = defaultFor(e.target.value);
                      if (p) (setPrinterId(p.id), setCopies(p.copies));
                    }}
                  >
                    {documents.map((d) => (
                      <option key={d.key} value={d.key}>
                        {d.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <Field label="Drucker" htmlFor="pr-printer">
                <Select id="pr-printer" value={printerId} onChange={(e) => setPrinterId(e.target.value)}>
                  {printers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.location ? ` (${p.location})` : ''}
                      {p.defaultFor.includes(doc) ? ' – Standard' : ''}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Exemplare" htmlFor="pr-copies">
                <Input id="pr-copies" type="number" min={1} max={20} value={copies} onChange={(e) => setCopies(Math.min(20, Math.max(1, Number(e.target.value) || 1)))} className="w-24" />
              </Field>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => print.mutate()} loading={print.isPending} disabled={!printerId || !doc}>
            <PrinterIcon /> Drucken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Schaltfläche „Drucken“ – nur sichtbar, wenn Drucker eingerichtet sind. */
export function PrintButton({ documents, target, size = 'sm' }: { documents: { key: string; label: string }[]; target: { repairId?: string; contractId?: string; ticketId?: string }; size?: 'sm' | 'default' }) {
  const { data } = usePrinters();
  const [open, setOpen] = useState(false);
  if (!data || data.items.filter((p) => p.active).length === 0) return null;
  return (
    <>
      <Button variant="outline" size={size} onClick={() => setOpen(true)}>
        <PrinterIcon /> Drucken
      </Button>
      <PrintDialog open={open} onClose={() => setOpen(false)} documents={documents} target={target} />
    </>
  );
}

/** Statusabfrage für Kunden (QR-Code auf dem Annahmeschein): Link und PIN für Rückfragen am Telefon. */
export function PublicStatusCard({ repairId, status, onChange }: { repairId: string; status: { url: string; pin: string } | null; onChange: () => void }) {
  const { can } = useAuth();
  const [confirmDialog, confirm] = useConfirm();
  const reset = useMutation({
    mutationFn: () => api(`/repairs/${repairId}/public-access/reset`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Neue PIN erzeugt – bitte Annahmeschein neu drucken');
      onChange();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!status) return null;
  return (
    <Card>
      {confirmDialog}
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <KeyRound className="size-4" /> Statusabfrage für Kunden
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2 text-sm">
        <p>
          PIN <span className="font-mono text-base font-semibold tracking-widest">{status.pin}</span>
        </p>
        <p className="text-xs text-muted-foreground">Steht mit dem QR-Code auf dem Annahmeschein. Ohne Anmeldung abrufbar.</p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigator.clipboard.writeText(status.url).then(() => toast.success('Link kopiert'), () => toast.error('Kopieren nicht möglich'))}
          >
            <Copy /> Link kopieren
          </Button>
          {can('repairs.intake') && (
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => (await confirm({ title: 'Neue PIN erzeugen?', description: 'Der bisherige QR-Code und die alte PIN funktionieren danach nicht mehr.', confirmLabel: 'Neue PIN' })) && reset.mutate()}
            >
              <RefreshCw /> Neue PIN
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
